import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { IslandDefinition, Quality } from '../game/types';
import type { Assets } from './models';

export const WILDLIFE_LIMITS = {
  standard: { islandBirds: 6, openBirds: 2, fish: 3, fishPeriod: 21 },
  low: { islandBirds: 3, openBirds: 1, fish: 1, fishPeriod: 27 },
} as const;
const FLIGHT_DURATION = 1.9;

interface Bird { x: number; z: number; radius: number; height: number; phase: number; speed: number }
interface Fish { cycle: number; x: number; z: number; dx: number; dz: number; valid: boolean }

function tintGeometry(geometry: THREE.BufferGeometry, color: THREE.Color): THREE.BufferGeometry {
  geometry.deleteAttribute('uv');
  const colors = new Float32Array(geometry.getAttribute('position').count * 3);
  for (let i = 0; i < colors.length; i += 3) { colors[i] = color.r; colors[i + 1] = color.g; colors[i + 2] = color.b; }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3)); return geometry;
}

function birdBody(): THREE.BufferGeometry {
  const white = new THREE.Color(0xefeee6), charcoal = new THREE.Color(0x263647), gold = new THREE.Color(0xcb984d);
  const body = tintGeometry(new THREE.CapsuleGeometry(.13, .56, 3, 6).rotateX(Math.PI / 2), white);
  const beak = tintGeometry(new THREE.ConeGeometry(.065, .21, 5).rotateX(Math.PI / 2).translate(0, 0, .54), gold);
  const tail = tintGeometry(new THREE.ConeGeometry(.17, .35, 4).rotateX(-Math.PI / 2).scale(1, .32, 1).translate(0, 0, -.56), charcoal);
  const merged = mergeGeometries([body, beak, tail])!;
  body.dispose(); beak.dispose(); tail.dispose(); return merged;
}

function birdWing(): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry(), colors: number[] = [];
  geometry.setAttribute('position', new THREE.Float32BufferAttribute([
    0, 0, .06, .48, .07, .14, .83, .08, -.01, 1.18, -.01, -.25, 1.65, -.09, -.62,
    .94, -.025, -.34, .57, -.01, -.31, .05, -.01, -.21,
  ], 3));
  for (let i = 0; i < 8; i++) { const color = new THREE.Color(i === 4 || i === 3 ? 0x3d4e5c : 0xf6f2e8); colors.push(color.r, color.g, color.b); }
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex([0, 1, 7, 1, 6, 7, 1, 2, 6, 2, 5, 6, 2, 3, 5, 3, 4, 5]); geometry.computeVertexNormals(); return geometry;
}

function flyingFish(): THREE.BufferGeometry {
  const positions: number[] = [], colors: number[] = [], indices: number[] = [], sections = [0, .105, .17, .12, 0];
  const back = new THREE.Color(0x377f9c), belly = new THREE.Color(0xd9edf0), fin = new THREE.Color(0x8dcacb);
  for (let ring = 0; ring < sections.length; ring++) for (let i = 0; i < 8; i++) {
    const angle = i / 8 * Math.PI * 2;
    positions.push(Math.sin(angle) * sections[ring], Math.cos(angle) * sections[ring] * .7, -.7 + ring * .31);
    const color = back.clone().lerp(belly, (1 - Math.cos(angle)) / 2); colors.push(color.r, color.g, color.b);
    if (ring < sections.length - 1) { const a = ring * 8 + i, b = ring * 8 + (i + 1) % 8; indices.push(a, a + 8, b, b, a + 8, b + 8); }
  }
  const triangle = (a: number[], b: number[], c: number[]) => {
    const index = positions.length / 3; positions.push(...a, ...b, ...c); indices.push(index, index + 1, index + 2);
    for (let i = 0; i < 3; i++) colors.push(fin.r, fin.g, fin.b);
  };
  for (const side of [-1, 1]) {
    triangle([side * .09, 0, .18], [side * .76, .075, -.13], [side * .13, -.025, -.38]);
    triangle([0, 0, -.57], [side * .25, .03, -.94], [0, .03, -.80]);
  }
  const geometry = new THREE.BufferGeometry(); geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3)); geometry.setIndex(indices); geometry.computeVertexNormals(); return geometry;
}

/** Sparse decorative wildlife; neither its position nor its clock feeds simulation. */
export class OceanWildlife {
  private readonly bodies: THREE.InstancedMesh;
  private readonly rightWings: THREE.InstancedMesh;
  private readonly leftWings: THREE.InstancedMesh;
  private readonly fishMesh: THREE.InstancedMesh;
  private readonly pose = new THREE.Object3D();
  private readonly orientation = new THREE.Quaternion();
  private readonly flap = new THREE.Quaternion();
  private readonly axis = new THREE.Vector3(0, 0, 1);
  private readonly birds: Bird[] = [];
  private readonly fish: Fish[] = [];
  private islands: IslandDefinition[] = [];
  private time = 0;
  private birdCount = 0;
  private fishCount = 0;
  private disposed = false;

  constructor(private readonly scene: THREE.Scene, assets: Assets, private quality: Quality) {
    const birdMaterial = assets.material(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .85, side: THREE.DoubleSide }));
    const fishMaterial = assets.material(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .42, metalness: .3, side: THREE.DoubleSide }));
    const wing = assets.geometry(birdWing());
    this.bodies = new THREE.InstancedMesh(assets.geometry(birdBody()), birdMaterial, 6);
    const leftWing = assets.geometry(wing.clone().scale(-1, 1, 1)); leftWing.computeVertexNormals();
    this.rightWings = new THREE.InstancedMesh(wing, birdMaterial, 6); this.leftWings = new THREE.InstancedMesh(leftWing, birdMaterial, 6);
    this.fishMesh = new THREE.InstancedMesh(assets.geometry(flyingFish()), fishMaterial, 3);
    this.bodies.name = 'ocean-seabird-bodies'; this.rightWings.name = 'ocean-seabird-right-wings';
    this.leftWings.name = 'ocean-seabird-left-wings'; this.fishMesh.name = 'ocean-flying-fish';
    for (const mesh of [this.bodies, this.rightWings, this.leftWings, this.fishMesh]) {
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage); mesh.frustumCulled = false; mesh.count = 0; scene.add(mesh);
    }
    for (let i = 0; i < 3; i++) this.fish.push({ cycle: -1, x: 0, z: 0, dx: 0, dz: 0, valid: false });
  }

  setMap(islands: IslandDefinition[]): void {
    this.islands = islands.map(island => ({ ...island })); this.birds.length = 0;
    for (let i = 0; i < 6; i++) {
      const island = islands[i % Math.max(1, islands.length)];
      this.birds.push({ x: island?.x ?? (i % 2 ? 30 : -28), z: island?.z ?? 10,
        radius: island ? island.radius * .7 + 8 + i % 2 * 3 : 22 + i * 3,
        height: island ? 13 + island.height * .48 + i % 3 * 2 : 15 + i % 2 * 3,
        phase: i * 2.399, speed: .16 + i % 3 * .024 });
    }
    this.reset();
  }

  setQuality(quality: Quality): void { this.quality = quality; }

  reset(): void {
    this.time = 0; this.fishCount = 0; this.fishMesh.count = 0;
    this.fish.forEach(fish => { fish.cycle = -1; fish.valid = false; });
  }

  private routeFish(fish: Fish, index: number, cycle: number, focus: THREE.Vector3): void {
    fish.valid = false;
    for (let attempt = 0; attempt < 8; attempt++) {
      const angle = index * 2.4 + cycle * 1.37 + attempt * .79;
      const x = focus.x + Math.sin(angle) * 27, z = focus.z + Math.cos(angle) * 27;
      const dx = Math.cos(angle + .2) * 16, dz = -Math.sin(angle + .2) * 16;
      const clear = this.islands.every(island => {
        const t = THREE.MathUtils.clamp(((island.x - x) * dx + (island.z - z) * dz) / (dx * dx + dz * dz), 0, 1);
        return Math.hypot(x + t * dx - island.x, z + t * dz - island.z) > island.radius + 4;
      });
      if (clear) { fish.x = x; fish.z = z; fish.dx = dx; fish.dz = dz; fish.valid = true; return; }
    }
  }

  update(dt: number, focus: THREE.Vector3): void {
    if (this.disposed) return;
    this.time += Math.max(0, dt);
    const limits = WILDLIFE_LIMITS[this.quality]; this.birdCount = this.islands.length ? limits.islandBirds : limits.openBirds;
    this.bodies.count = this.rightWings.count = this.leftWings.count = this.birdCount;
    for (let i = 0; i < this.birdCount; i++) {
      const bird = this.birds[i]; if (!bird) continue;
      const angle = bird.phase + this.time * bird.speed, x = bird.x + Math.sin(angle) * bird.radius, z = bird.z + Math.cos(angle) * bird.radius * .78;
      const heading = Math.atan2(Math.cos(angle), -Math.sin(angle) * .78), scale = .95 + i % 3 * .1;
      this.pose.position.set(x, bird.height + Math.sin(this.time * .9 + i) * .65, z);
      this.pose.rotation.set(Math.sin(this.time * .7 + i) * .045, heading, -.15, 'YXZ');
      this.orientation.copy(this.pose.quaternion); this.pose.scale.setScalar(scale); this.pose.updateMatrix(); this.bodies.setMatrixAt(i, this.pose.matrix);
      const wingAngle = -.06 + Math.sin(this.time * 5.3 + i * 1.7) * .37 * (.5 + Math.sin(this.time * .7 + i) * .5);
      for (const [mesh, side] of [[this.rightWings, 1], [this.leftWings, -1]] as const) {
        this.flap.setFromAxisAngle(this.axis, wingAngle * side); this.pose.quaternion.copy(this.orientation).multiply(this.flap);
        this.pose.scale.setScalar(scale); this.pose.updateMatrix(); mesh.setMatrixAt(i, this.pose.matrix);
      }
    }
    this.bodies.instanceMatrix.needsUpdate = this.rightWings.instanceMatrix.needsUpdate = this.leftWings.instanceMatrix.needsUpdate = true;
    this.fishCount = 0;
    for (let i = 0; i < limits.fish; i++) {
      const elapsed = this.time - 4 - i * 6.5;
      if (elapsed < 0) continue;
      const cycle = Math.floor(elapsed / limits.fishPeriod), age = elapsed - cycle * limits.fishPeriod, fish = this.fish[i];
      if (cycle !== fish.cycle) { fish.cycle = cycle; this.routeFish(fish, i, cycle, focus); }
      if (age > FLIGHT_DURATION || !fish.valid) continue;
      const t = age / FLIGHT_DURATION, y = -.33 + 4 * t * (1 - t) * 3.7;
      this.pose.position.set(fish.x + fish.dx * t, y, fish.z + fish.dz * t);
      this.pose.rotation.set(-Math.atan2(3.7 * 4 * (1 - 2 * t), 16), Math.atan2(fish.dx, fish.dz), Math.sin(age * 17) * .08, 'YXZ');
      this.pose.scale.setScalar(1.25); this.pose.updateMatrix(); this.fishMesh.setMatrixAt(this.fishCount++, this.pose.matrix);
    }
    this.fishMesh.count = this.fishCount; this.fishMesh.instanceMatrix.needsUpdate = true;
  }

  getStats(): { seabirds: number; flyingFish: number } { return { seabirds: this.birdCount, flyingFish: this.fishCount }; }

  dispose(): void {
    if (this.disposed) return; this.disposed = true;
    for (const mesh of [this.bodies, this.rightWings, this.leftWings, this.fishMesh]) { this.scene.remove(mesh); mesh.dispose(); }
    this.birds.length = 0; this.fish.length = 0; this.islands.length = 0;
    // The shared Assets owner releases the four geometries and two materials.
  }
}
